import { useId } from 'react';
import { cn } from '@/utils/cn';

interface DailyRewardBeamProps {
  className?: string;
}

const BEAM_COLOR = '#D42F55';

export function DailyBonusBeamInactive({ className }: DailyRewardBeamProps) {
  const rawId = useId();
  const safeId = rawId.replace(/:/g, '');

  const clipId = `daily-reward-beam-clip-${safeId}`;
  const glowId = `daily-reward-beam-glow-${safeId}`;
  const glowBlurId = `daily-reward-beam-glow-blur-${safeId}`;
  const coreId = `daily-reward-beam-core-${safeId}`;
  const coreBlurId = `daily-reward-beam-core-blur-${safeId}`;
  const coneBlurId = `daily-reward-beam-cone-blur-${safeId}`;
  const haloId = `daily-reward-beam-halo-${safeId}`;
  const haloBlurId = `daily-reward-beam-halo-blur-${safeId}`;
  const rayRightId = `daily-reward-beam-ray-right-${safeId}`;
  const rayRightBlurId = `daily-reward-beam-ray-right-blur-${safeId}`;
  const rayLeftId = `daily-reward-beam-ray-left-${safeId}`;
  const rayLeftBlurId = `daily-reward-beam-ray-left-blur-${safeId}`;

  return (
    <span
      aria-hidden
      className={cn('pointer-events-none absolute -inset-px overflow-hidden rounded-[inherit] mix-blend-plus-lighter', className)}
    >
      <svg
        width="90"
        height="142"
        viewBox="0 0 90 142"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="absolute top-0 left-0 w-full h-auto"
      >
        <g opacity="0.7">
          <ellipse
            cx="45"
            cy="51.35"
            rx="52"
            ry="31.34"
            fill={`url(#${glowId})`}
            filter={`url(#${glowBlurId})`}
            className="mix-blend-plus-lighter"
          />
          <g clipPath={`url(#${clipId})`}>
            <path d="M37.87-39.79h10.56l14.75 98.53H23.12z" fill={`url(#${coreId})`} filter={`url(#${coneBlurId})`} opacity="0.5" className="mix-blend-plus-lighter" />
            <path d="M37.87-39.79h10.56v98.53H37.87z" fill={`url(#${coreId})`} filter={`url(#${coreBlurId})`} className="mix-blend-plus-lighter" />
            <path d="M36.44-53.09h13.42l37.45 125.12H-1z" fill={`url(#${haloId})`} filter={`url(#${haloBlurId})`} className="mix-blend-plus-lighter" />
            <path d="M59.28-25.58l-1.31 65.29" stroke={`url(#${rayRightId})`} strokeWidth="0.22" filter={`url(#${rayRightBlurId})`} className="mix-blend-plus-lighter" />
            <path d="M26.1-37.82v53.45" stroke={`url(#${rayLeftId})`} strokeWidth="0.22" filter={`url(#${rayLeftBlurId})`} opacity="0.3" className="mix-blend-plus-lighter" />
          </g>
        </g>
        <defs>
          <clipPath id={clipId}>
            <rect width="90" height="86" />
          </clipPath>
          <filter id={glowBlurId} x="-51.1" y="-24.09" width="192.2" height="150.87" filterUnits="userSpaceOnUse">
            <feGaussianBlur stdDeviation="22.05" />
          </filter>
          <filter id={coneBlurId} x="15.4" y="-47.52" width="55.5" height="113.98" filterUnits="userSpaceOnUse">
            <feGaussianBlur stdDeviation="3.86" />
          </filter>
          <filter id={coreBlurId} x="17.87" y="-59.79" width="50.56" height="138.53" filterUnits="userSpaceOnUse">
            <feGaussianBlur stdDeviation="10" />
          </filter>
          <filter id={haloBlurId} x="-15.72" y="-67.8" width="117.74" height="154.55" filterUnits="userSpaceOnUse">
            <feGaussianBlur stdDeviation="7.36" />
          </filter>
          <filter id={rayRightBlurId} x="56.1" y="-27.34" width="5.06" height="68.82" filterUnits="userSpaceOnUse">
            <feGaussianBlur stdDeviation="0.88" />
          </filter>
          <filter id={rayLeftBlurId} x="24.45" y="-39.36" width="3.31" height="56.53" filterUnits="userSpaceOnUse">
            <feGaussianBlur stdDeviation="0.77" />
          </filter>
          <radialGradient
            id={glowId}
            cx="0"
            cy="0"
            r="1"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(45 48.18) rotate(90) scale(43.56 146.41)"
          >
            <stop stopColor="#ED6D8B" stopOpacity="0.4" />
            <stop offset="1" stopColor={BEAM_COLOR} stopOpacity="0.4" />
          </radialGradient>
          <linearGradient id={coreId} x1="0" y1="-39.79" x2="0" y2="53.76" gradientUnits="userSpaceOnUse">
            <stop stopColor={BEAM_COLOR} />
            <stop offset="1" stopColor={BEAM_COLOR} stopOpacity="0" />
          </linearGradient>
          <linearGradient id={haloId} x1="0" y1="-53.09" x2="0" y2="65.71" gradientUnits="userSpaceOnUse">
            <stop stopColor={BEAM_COLOR} />
            <stop offset="1" stopColor={BEAM_COLOR} stopOpacity="0" />
          </linearGradient>
          <linearGradient id={rayRightId} x1="0" y1="-35.15" x2="0" y2="8.86" gradientUnits="userSpaceOnUse">
            <stop stopColor={BEAM_COLOR} />
            <stop offset="1" stopColor={BEAM_COLOR} stopOpacity="0" />
          </linearGradient>
          <linearGradient id={rayLeftId} x1="0" y1="-9.94" x2="0" y2="34.08" gradientUnits="userSpaceOnUse">
            <stop stopColor={BEAM_COLOR} />
            <stop offset="1" stopColor={BEAM_COLOR} stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
    </span>
  );
}
